import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useContent } from "@/context/ContentProvider";
import { useLocation, useNavigate } from "react-router-dom";

const CONTACT_URL = "mailto:info@mayr-technologies.com";

const FALLBACK = {
  logo: "Mayr",
  links: [
    { label: "Services", href: "#services" },
    { label: "Projects", href: "#projects" },
    { label: "Infrastructure", href: "#infrastructure" },
    { label: "About", href: "#about" },
  ],
  contact_text: "Contact",
  contact_email: "info@mayr-technologies.com",
};

const Navbar = () => {
  const { data } = useContent("navbar");
  const nav = data || FALLBACK;
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#030303]/80 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8 flex items-center justify-between h-32 md:h-36">
        <a
          href={isHomePage ? "#" : "/"}
          data-testid="navbar-logo"
          className="focus:outline-none focus:ring-1 focus:ring-white/50 rounded"
          onClick={(e) => {
            e.preventDefault();
            if (isHomePage) {
              window.scrollTo({ top: 0, behavior: "smooth" });
              return;
            }
            navigate("/");
          }}
        >
          <img src="/mayr-logo-transparent.png?v=6" alt="Mayr Technologies" className="h-[70px] w-auto object-contain" />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {nav.links.map((link) => (
            <a
              key={link.label}
              href={isHomePage ? link.href : `/${link.href}`}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="text-sm text-neutral-500 font-['Manrope'] font-medium tracking-tight
                transition-colors duration-300 hover:text-white
                focus:outline-none focus:ring-1 focus:ring-white/50 rounded"
            >
              {link.label}
            </a>
          ))}
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-contact-button"
            className="text-sm text-white font-['Manrope'] font-medium tracking-tight px-5 py-2 rounded-full border border-white/[0.15]
              transition-all duration-300 hover:border-white/[0.35] hover:bg-white/[0.05]
              focus:outline-none focus:ring-1 focus:ring-white/50"
          >
            {nav.contact_text}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-neutral-400 transition-colors duration-300 hover:text-white
            focus:outline-none focus:ring-1 focus:ring-white/50 rounded"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#030303]/95 backdrop-blur-xl border-b border-white/[0.06] px-6 pb-6"
        >
          <div className="flex flex-col gap-4 pt-2">
            {nav.links.map((link) => (
              <a
                key={link.label}
                href={isHomePage ? link.href : `/${link.href}`}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className="text-base text-neutral-400 font-['Manrope'] font-medium
                  transition-colors duration-300 hover:text-white py-2"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-contact"
              className="text-base text-white font-['Manrope'] font-medium py-2"
              onClick={() => setMobileOpen(false)}
            >
              {nav.contact_text}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
